import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CompanyLegalDetails } from '../components/CompanyLegalDetails';
import { ETAPPIO_COMPANY } from '../constants/company';
import { systemApi, type TermsOfServiceDocument } from '../services/api';

const formatSavedDate = (value: string) =>
  new Intl.DateTimeFormat('sv-SE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(new Date(value));

export function TermsOfService() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  // `?version=N` shows exactly the version a user was shown when accepting.
  const requestedVersion = searchParams.get('version')?.trim() || null;
  const [document, setDocument] = useState<TermsOfServiceDocument | null>(null);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    window.document.title = 'Användarvillkor för Etappio';
    let cancelled = false;
    setDocument(null);
    setFailed(false);
    const load = requestedVersion
      ? Promise.all([
          systemApi.getTermsOfServiceVersion(requestedVersion),
          systemApi.getTermsOfService().then((current) => current.version).catch(() => null)
        ])
      : systemApi.getTermsOfService().then((current) => [current, current.version] as const);
    load
      .then(([shown, current]) => {
        if (cancelled) return;
        setDocument(shown);
        setCurrentVersion(current);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [requestedVersion]);

  const isEarlierVersion = Boolean(
    document && currentVersion && document.version !== currentVersion
  );

  return (
    <div className="min-vh-100 bg-light d-flex flex-column">
      <header className="bg-white border-bottom py-3">
        <div className="container d-flex align-items-center justify-content-between">
          <Link to="/" aria-label="Etappio startsida">
            <img src="/assets/images/logo-full.png" alt="ETAPPIO" height="38" />
          </Link>
          <Link to="/" className="btn btn-light-brand btn-sm">
            Till startsidan
          </Link>
        </div>
      </header>

      <main className="container py-5 flex-grow-1" style={{ maxWidth: '920px' }}>
        <article className="card border-0 shadow-sm">
          <div className="card-body p-4 p-md-5">
            <h1 className="fw-bold mb-2">Användarvillkor för Etappio</h1>

            {!document && !failed && (
              <div className="d-flex align-items-center gap-2 text-muted py-5" role="status">
                <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                Hämtar aktuella Användarvillkor…
              </div>
            )}

            {failed && requestedVersion && (
              <div className="alert alert-danger mt-4" role="alert">
                {t('termsVersions.public.versionMissing', { version: requestedVersion })}{' '}
                <Link to="/anvandarvillkor">{t('termsVersions.public.showCurrent')}</Link>
              </div>
            )}

            {failed && !requestedVersion && (
              <div className="alert alert-danger mt-4" role="alert">
                Aktuella Användarvillkor kan inte visas just nu. Försök igen senare eller kontakta{' '}
                <a href={`mailto:${ETAPPIO_COMPANY.email}`}>{ETAPPIO_COMPANY.email}</a>.
              </div>
            )}

            {document && (
              <>
                {isEarlierVersion && (
                  <div className="alert alert-warning" role="status" data-testid="terms-earlier-version">
                    {t('termsVersions.public.earlierVersion', { version: currentVersion })}{' '}
                    <Link to="/anvandarvillkor">{t('termsVersions.public.showCurrent')}</Link>
                  </div>
                )}
                <p className="text-muted border-bottom pb-3 mb-4" data-testid="terms-version">
                  {document.termsTitle && <>{document.termsTitle} · </>}
                  Version {document.version} · Senast uppdaterad{' '}
                  <time dateTime={document.savedAt}>
                    {formatSavedDate(document.savedAt)}
                  </time>
                  {document.sha256 && (
                    <span className="d-block small text-break mt-1" data-testid="terms-checksum">
                      {t('termsVersions.checksum')}: <code>{document.sha256}</code>
                    </span>
                  )}
                </p>
                <div
                  className="ql-editor p-0"
                  data-testid="terms-content"
                  dangerouslySetInnerHTML={{ __html: document.content }}
                />
                <aside className="alert alert-primary mt-4 mb-0" data-testid="refund-policy-link">
                  <strong>Betalning, avbokning eller återbetalning?</strong>{' '}
                  Läs vår{' '}
                  <Link to="/aterbetalning-och-avbokning">
                    återbetalnings- och avbokningspolicy
                  </Link>{' '}
                  för en tydlig genomgång av projektmedel, avgifter, etappmål och ångerrätt.
                </aside>
                <aside className="alert alert-info mt-3 mb-0" data-testid="delivery-information-link">
                  <strong>Vem ansvarar för leveransen?</strong>{' '}
                  Läs vår{' '}
                  <Link to="/leveransinformation">
                    information om leveranser och ansvar
                  </Link>{' '}
                  för en praktisk sammanfattning av Etappios, entreprenörens och kundens roller.
                </aside>
              </>
            )}
          </div>
        </article>
      </main>

      <footer className="bg-dark text-white py-4">
        <div className="container text-center">
          <p className="small text-white-50 mb-2">© 2026 {ETAPPIO_COMPANY.legalName}</p>
          <CompanyLegalDetails variant="compact" theme="dark" />
        </div>
      </footer>
    </div>
  );
}
